// См. docs/data-layer.md §4.

import { useCallback, useRef } from 'react'

import {
  useMutation as useReactMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query'

import { HttpError } from '../http/HttpError'
import { httpRequest } from '../http/httpClient'
import type { HttpMethod } from '../http/types'
import type { OptimisticSnapshot, OptimisticUpdate } from './optimistic'
import {
  createShouldRunSideEffect,
  runErrorSideEffects,
  runInvalidateQueries,
  runSuccessSideEffects,
} from './sideEffects'
import type { DisabledSideEffectsConfig, MutationSideEffects, QueryContext } from './types'

// Опции жизненного цикла react-query (onSuccess/onError/onMutate/onSettled) намеренно исключены:
// их роль полностью берут на себя sideEffects (onSuccess/onErrorWithCode/onHttpError/onAnyError),
// чтобы имена не конфликтовали и обработка ошибок всегда шла через единый канал.
type NativeMutationOptions<TInput, TData> = Omit<
  UseMutationOptions<TData, HttpError, TInput>,
  'mutationFn' | 'mutationKey' | 'onSuccess' | 'onError' | 'onMutate' | 'onSettled'
>

export type CreateMutationConfig<
  TInput,
  TApiOutput,
  TResult,
  TContext extends object = Record<string, never>,
> = {
  key: string
  path: string | ((input: TInput) => string)
  /** По умолчанию POST */
  method?: Exclude<HttpMethod, 'GET'>
  /** Клиентский тип → тело запроса */
  getBody?: (input: TInput) => unknown
  getSearchParams?: (input: TInput) => Record<string, unknown>
  /** Ответ → клиентский тип. Обязателен всегда */
  transform: (apiOutput: TApiOutput, input: TInput) => TResult
  /**
   * Правки кэша до ответа сервера — собираются хелпером `optimisticUpdate` (docs/data-layer.md §4.1).
   * Пока список пуст, мутация ведёт себя ровно как раньше.
   */
  optimistic?: OptimisticUpdate<TInput>[]
  sideEffects?: MutationSideEffects<TResult, TInput, TContext>
  useAdditionalContext?: () => TContext
  mocked?: boolean
  /** Добавляет заголовок `Idempotency-Key` и включает ретраи */
  idempotency?: boolean
  mutationOptions?: NativeMutationOptions<TInput, TResult>
}

export type RuntimeMutationOptions<
  TResult,
  TInput,
  TContext extends object = Record<string, never>,
> = NativeMutationOptions<TInput, TResult> &
  MutationSideEffects<TResult, TInput, TContext> & {
    disabledSideEffects?: DisabledSideEffectsConfig
  }

export type MutationFactory<TInput, TResult, TContext extends object = Record<string, never>> = {
  useMutation: (
    runtimeOptions?: RuntimeMutationOptions<TResult, TInput, TContext>,
  ) => readonly [
    (input: TInput) => Promise<TResult | undefined>,
    UseMutationResult<TResult, HttpError, TInput>,
  ]
}

const generateIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export function createMutation<
  TInput = void,
  TApiOutput = unknown,
  TResult = TApiOutput,
  TContext extends object = Record<string, never>,
>(
  config: CreateMutationConfig<TInput, TApiOutput, TResult, TContext>,
): MutationFactory<TInput, TResult, TContext> {
  const method = config.method ?? 'POST'
  const optimisticUpdates = config.optimistic ?? []
  const isOptimistic = optimisticUpdates.length > 0

  const performMutation = async (input: TInput, idempotencyKey?: string): Promise<TResult> => {
    const apiOutput = await httpRequest<TApiOutput, TInput>({
      path: config.path,
      method,
      input,
      getSearchParams: config.getSearchParams,
      getBody: config.getBody,
      mocked: config.mocked,
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    })

    return config.transform(apiOutput, input)
  }

  const useMutationHook = (runtimeOptions?: RuntimeMutationOptions<TResult, TInput, TContext>) => {
    const reactQueryClient = useQueryClient()
    const additionalContext = (config.useAdditionalContext?.() ?? {}) as TContext
    const idempotencyKeyRef = useRef<string | undefined>(undefined)

    const {
      disabledSideEffects,
      onErrorWithCode,
      onHttpError,
      onAnyError,
      onSuccess,
      invalidateQueries,
      ...restOptions
    } = (runtimeOptions ?? {}) as RuntimeMutationOptions<TResult, TInput, TContext>

    const runtimeSideEffects: MutationSideEffects<TResult, TInput, TContext> = {
      onErrorWithCode,
      onHttpError,
      onAnyError,
      onSuccess,
      invalidateQueries,
    }
    const shouldRun = createShouldRunSideEffect(disabledSideEffects)

    const buildContext = (input: TInput): QueryContext<TInput, TContext> =>
      Object.assign({ input, queryClient: reactQueryClient }, additionalContext) as QueryContext<
        TInput,
        TContext
      >

    /**
     * «Я — последняя незавершённая мутация этой ручки». react-query считает текущую мутацию
     * pending и внутри `onError`/`onSettled` (статус проставляется уже после колбэков),
     * поэтому единица — это только я сама.
     *
     * На этой проверке держится вся защита от быстрого перещёлкивания: пока в полёте есть
     * более свежие запросы, ни откатывать кэш, ни инвалидировать его нельзя — и то и другое
     * вернуло бы человеку значение, которое он уже отменил.
     */
    const isLastPendingMutation = (): boolean =>
      reactQueryClient.isMutating({ mutationKey: [config.key] }) <= 1

    const mutationResult = useReactMutation<TResult, HttpError, TInput, OptimisticSnapshot[]>({
      mutationKey: [config.key],
      mutationFn: (input: TInput) => performMutation(input, idempotencyKeyRef.current),
      retry: config.idempotency ? 1 : false,
      ...config.mutationOptions,
      ...restOptions,
      onMutate: async (input) => {
        const snapshots: OptimisticSnapshot[] = []

        for (const update of optimisticUpdates) {
          const queryKey = update.getQueryKey(input)

          // Летящий рефетч допишет в кэш ответ, собранный ДО правки, и тумблер прыгнет назад.
          await reactQueryClient.cancelQueries({ queryKey })

          const previousData = reactQueryClient.getQueryData(queryKey)
          snapshots.push({ queryKey, previousData })

          // Данных ещё нет — оптимистично показывать нечего, дожидаемся ответа.
          if (previousData === undefined) continue

          reactQueryClient.setQueryData(queryKey, update.getNextData(previousData, input))
        }

        return snapshots
      },
      onSuccess: async (data, input) => {
        const ctx = buildContext(input)

        runSuccessSideEffects(data, ctx, config.sideEffects, runtimeSideEffects, shouldRun)

        // У оптимистичных ручек инвалидация переехала в onSettled: она обязана случиться и
        // после ошибки (починить откат), и ровно один раз на всю очередь быстрых нажатий.
        if (!isOptimistic) {
          await runInvalidateQueries(ctx, config.sideEffects, runtimeSideEffects, shouldRun)
        }
      },
      onError: (error, input, snapshots) => {
        // Откатываем только если свежее меня в полёте ничего нет. Иначе вернули бы значение,
        // снятое до соседнего нажатия, — то самое «свитчер живёт своей жизнью».
        if (snapshots && isLastPendingMutation()) {
          for (const { queryKey, previousData } of snapshots) {
            if (previousData === undefined) continue
            reactQueryClient.setQueryData(queryKey, previousData)
          }
        }

        runErrorSideEffects(
          error,
          buildContext(input),
          config.sideEffects,
          runtimeSideEffects,
          shouldRun,
        )
      },
      onSettled: async (_data, _error, input) => {
        if (!isOptimistic || !isLastPendingMutation()) return

        // Последнее слово всегда за сервером: после очереди нажатий перечитываем правду один раз.
        await runInvalidateQueries(
          buildContext(input),
          config.sideEffects,
          runtimeSideEffects,
          shouldRun,
        )
      },
    })

    const { mutateAsync } = mutationResult

    // `mutate` асинхронный и не бросает: ошибка уже обработана в sideEffects и лежит в state.error.
    // Если нужен throw — есть state.mutateAsync.
    const mutate = useCallback(
      async (input: TInput): Promise<TResult | undefined> => {
        idempotencyKeyRef.current = config.idempotency ? generateIdempotencyKey() : undefined

        try {
          return await mutateAsync(input)
        } catch {
          return undefined
        }
      },
      [mutateAsync],
    )

    return [mutate, mutationResult] as const
  }

  return { useMutation: useMutationHook }
}
